// @noflow

import React, { useState, useEffect } from "react";
import { FormattedMessage } from "react-intl";
import { Modal } from "react-bootstrap";
import { translate } from "../../../components/IntlProviderWrapper";
import type { IPreplanningOverrideDataLine } from "../../../lib/backend/manufacturing2.generated.types";
import { SHIFT_MODIFIERS } from "../../../lib/Models";
import { classNames, fromShiftTag, niceDate, shiftNumber } from "../../../lib/Util";
import ErrorComponent from "../../../components/ErrorComponent";
import ConfirmationModal from "../../../components/ConfirmationModal";

import "./PreplanningOverrideShiftModal.scss";

const PreplanningOverrideShiftModal = (props: {
    show: boolean;
    onHide: () => void;
    onUpdateShiftData: (shift: IPreplanningOverrideDataLine) => void;
    shift: IPreplanningOverrideDataLine;
    line_external_id: string;
    canSave: boolean;
    has_override: boolean;
}) => {
    const { quantity, comment, type, shift_tag } = props.shift;
    const line_external_id = props.line_external_id;

    const [override_quantity, setOverrideQuantity] = useState<number | string>(quantity);
    const [new_comment, setNewComment] = useState(comment);
    const [new_type, setNewType] = useState(type);
    const [quantity_error, setQuantityError] = useState("");
    const [confirmationVisible, setConfirmationVisible] = useState(false);

    const shift_date = fromShiftTag(shift_tag);
    const shift_number = shiftNumber(shift_date);

    useEffect(() => {
        setOverrideQuantity(props.shift.quantity);
        setNewComment(props.shift.comment);
        setNewType(props.shift.type);
        setQuantityError("");
    }, [props.shift]);

    const validateQuantity = () => {
        if (override_quantity === "") {
            setQuantityError(translate("common.required_field", "Required field"));
            return false;
        }

        if (Number.isNaN(override_quantity) || Number(override_quantity) < 0) {
            setQuantityError(translate("common.enter_positive_number", "Enter a positive number"));
            return false;
        }

        setQuantityError("");
        return true;
    };

    const handleQuantityChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value;

        if (value === "") {
            setOverrideQuantity("");
            return;
        }

        const parsed_value = parseFloat(value);
        if (!isNaN(parsed_value) && parsed_value >= 0) {
            setOverrideQuantity(parsed_value);
        }
    };

    const handleCommentChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        const value = e.target.value;
        setNewComment(value);
    };

    const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const value = e.target.value;
        setNewType(value);
    };

    const handleClearAll = () => {
        setOverrideQuantity(0);
        setNewComment("");
        setNewType(SHIFT_MODIFIERS.none);
        setQuantityError("");
        setConfirmationVisible(false);
    };

    const handleRemoveShiftOverride = () => {
        props.onUpdateShiftData({
            shift_tag,
            quantity: 0,
            comment: "",
            type: SHIFT_MODIFIERS.none,
        });
        handleClose();
    };

    const handleClose = () => {
        handleClearAll();
        props.onHide();
    };

    const handleSave = () => {
        if (!validateQuantity()) {
            return;
        }

        props.onUpdateShiftData({
            shift_tag,
            quantity: Number(override_quantity),
            comment: new_comment.trim(),
            type: new_type,
        });
        handleClose();
    };

    return (
        <React.Fragment>
            <Modal show={props.show} onHide={props.onHide}>
                <Modal.Header>
                    <Modal.Title as={"div"}>
                        <span className="mr-2">{`${translate("common.title", "Title")}: ${line_external_id}`}</span>|
                        <span className="ml-2">{`${translate("common.shift", "Shift")}: ${shift_number.shift_day + 1} - ${niceDate(shift_date)}`}</span>
                    </Modal.Title>
                    <button type="button" className="close" onClick={handleClose}>
                        <span aria-hidden="true">×</span>
                    </button>
                </Modal.Header>
                <Modal.Body>
                    <div className="preplanning-override-shift-modal">
                        <div className="form-group">
                            <label htmlFor="override_quantity">
                                {translate("Preplanning.override_quantity", "Override quantity")}
                            </label>
                            <input
                                type="number"
                                className={classNames("form-control", { "is-invalid": quantity_error })}
                                id="override_quantity"
                                value={override_quantity}
                                onChange={handleQuantityChange}
                                min="0"
                                step="1"
                                placeholder={translate("common.quantity", "Quantity")}
                                disabled={!props.canSave}
                            />
                            {quantity_error && <div className="invalid-feedback d-block">{quantity_error}</div>}
                        </div>

                        <div className="form-group">
                            <label htmlFor="comment">{translate("common.comment", "Comment")}</label>
                            <textarea
                                className="form-control"
                                id="comment"
                                value={new_comment}
                                onChange={handleCommentChange}
                                rows={3}
                                maxLength={500}
                                placeholder={translate("common.comment", "Comment")}
                                disabled={!props.canSave}
                            />
                            <small className="form-text text-muted">
                                {new_comment ? `${new_comment.length}/500` : "0/500"}
                            </small>
                        </div>

                        <div className="form-group">
                            <label htmlFor="type">{translate("common.type", "Type")}</label>
                            <select
                                className="form-control"
                                id="type"
                                value={new_type}
                                onChange={handleTypeChange}
                                disabled={!props.canSave}
                            >
                                {(Object.values(SHIFT_MODIFIERS) as string[]).map((value: string) => (
                                    <option key={value} value={value}>
                                        {/* TODO - when we have actual values instead of dummy */}
                                        {translate(`common.${value}`, value)}
                                    </option>
                                ))}
                            </select>
                        </div>
                    </div>
                    {!props.canSave && (
                        <ErrorComponent
                            type="warning"
                            msg={translate(
                                "Preplanning.no_preplanning_lines_data",
                                "There is no data to override, recalculate quantities first."
                            )}
                        />
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button onClick={handleClose} className="btn btn-outline-secondary">
                        <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                    </button>
                    {props.has_override && (
                        <button
                            onClick={() => setConfirmationVisible(true)}
                            className="btn btn-outline-secondary"
                            disabled={!props.canSave}
                        >
                            <FormattedMessage id="common.reset" defaultMessage="Reset" />
                        </button>
                    )}
                    <button onClick={handleSave} className="btn btn-primary" disabled={!props.canSave}>
                        <FormattedMessage id="common.save" defaultMessage="Save" />
                    </button>
                </Modal.Footer>
            </Modal>
            <ConfirmationModal
                show={confirmationVisible}
                title={translate("common.reset", "Reset")}
                onCancel={() => setConfirmationVisible(false)}
                onAccept={handleRemoveShiftOverride}
            >
                <div>
                    {translate(
                        "Preplanning.confirm_reset_custom_settings",
                        "Are you sure you want to reset custom settings?"
                    )}
                </div>
            </ConfirmationModal>
        </React.Fragment>
    );
};

export default PreplanningOverrideShiftModal;
